import { createSelector } from 'reselect';
import {
  getSummary,
  getFormatting,
  getAnalysis,
  getTopShareholders,
} from '@/pages/CompanyReport/redux/selectors';
import { getPeople } from '@ducks/people/selectors';
import { get } from 'lodash';
import { format } from 'date-fns';
import { toSignificant, toCurrencyTruncated } from '@/utilities/formatting';
import type {
  Shareholder,
  OwnerType,
} from '@/pages/CompanyReport/redux/interface/interface';
import { calculateHoldingValue } from '../utils';
import { getLocation } from '@/redux/location/selectors';

interface InsiderTradingEntity {
  companySharesBought: number;
  companySharesSold: number;
  companyBuyCount: number;
  companySellCount: number;
  individualSharesBought: number;
  individualSharesSold: number;
  individualBuyCount: number;
  individualSellCount: number;
  totalBought: number;
  totalSold: number;
  totalBoughtValue: string;
  totalSoldValue: string;
}

interface ShareholderWithTitle extends Shareholder {
  holdingValue: string;
  sharesHeldFormat: string;
  owner: {
    name: string;
    type: OwnerType;
    companyType: number;
    objectId: number;
    teamTitle: string;
    boardTitle: string;
  };
}

interface OwnershipEntity {
  Institutions?: number;
  'Public Companies'?: number;
  'Private Companies'?: number;
  'Individual Insiders'?: number;
  'Company Controlled Foundation'?: number;
  'Employee Share Scheme'?: number;
  'State or Government'?: number;
  'Hedge Funds'?: number;
  'VC/PE Firms'?: number;
  'Sovereign Wealth Funds'?: number;
  'General Public'?: number;
}

interface OwnershipValues {
  shares: string;
  percent: string;
}

interface OwnershipDataFormatted {
  Institutions?: OwnershipValues;
  'Public Companies'?: OwnershipValues;
  'Private Companies'?: OwnershipValues;
  'Individual Insiders'?: OwnershipValues;
  'Company Controlled Foundation'?: OwnershipValues;
  'Employee Share Scheme'?: OwnershipValues;
  'State or Government'?: OwnershipValues;
  'Hedge Funds'?: OwnershipValues;
  'VC/PE Firms'?: OwnershipValues;
  'Sovereign Wealth Funds'?: OwnershipValues;
  'General Public'?: OwnershipValues;
}

type PartialInsiderTradingEntity = Pick<
  InsiderTradingEntity,
  | 'companySharesBought'
  | 'individualSharesBought'
  | 'totalBought'
  | 'totalBoughtValue'
  | 'companyBuyCount'
  | 'individualBuyCount'
>;

interface OwnershipProps {
  companyId: string;
  uniqueSymbol: string;
  topShareholders: ShareholderWithTitle[];
  totalOwnershipPercent: string;
  /**
   * Insider Trading
   */
  insiderTrading: InsiderTradingEntity[];
  /**
   * Ownership Chart
   */
  ownership: Partial<OwnershipEntity>[];
  totalOwnedShares: number;
  ownershipDataFormatted: OwnershipDataFormatted;
}

const getOwnershipSelector = createSelector(
  [
    getSummary,
    getFormatting,
    getAnalysis,
    getTopShareholders,
    getPeople,
    getLocation,
  ],
  (
    summaryNext,
    formatting,
    analysis,
    topShareholders,
    people,
    location
  ): OwnershipProps => {
    const locale = location.payload.locale || 'en';
    const topShareholdersSorted: Shareholder[] = Object.values(
      topShareholders
    ).sort((a, b) => a.rankSharesHeld - b.rankSharesHeld);

    const topShareholdersWithTitles = topShareholdersSorted.map(
      (shareholder) => {
        const person = people[shareholder.owner.objectId];
        const owner = shareholder.owner;
        const percentOfSharesOutstanding = parseFloat(
          toSignificant(shareholder.percentOfSharesOutstanding)
        );
        const holdingValue = calculateHoldingValue(
          shareholder,
          analysis,
          formatting
        );
        const percentSharesChanged = parseFloat(
          toSignificant(shareholder.percentSharesChanged, 2)
        );
        const percentOfPortfolio = parseFloat(
          toSignificant(shareholder.percentOfPortfolio, 2)
        );
        const sharesHeldFormat = shareholder.sharesHeld.toLocaleString(locale);
        const date = shareholder.holdingDate.split(/[^0-9]/);
        const newDate = new Date(
          parseInt(date[0]),
          parseInt(date[1]) - 1,
          parseInt(date[2])
        );
        return {
          ...shareholder,
          percentOfSharesOutstanding,
          holdingDate: format(newDate, 'dd MMM yy'),
          sharesHeldFormat,
          holdingValue,
          percentSharesChanged,
          percentOfPortfolio,
          owner: {
            ...owner,
            teamTitle: get(person, 'proTitle', ''),
            boardTitle: get(person, 'shortTitle', ''),
          },
        };
      }
    );
    const totalOwnershipPercent = toSignificant(
      topShareholdersSorted.reduce((acc, shareholder) => {
        return acc + shareholder.percentOfSharesOutstanding;
      }, 0)
    );
    const { legacyChartData } = analysis;

    /**
     * Ownership Chart
     */
    const ownership: Partial<OwnershipEntity>[] = [
      legacyChartData.ownershipBreakdown,
    ];
    const totalOwnedShares: number | undefined = Object.values(
      ownership[0]
    ).reduce((a = 0, b = 0) => a + b, 0);
    const ownershipDataFormatted = Object.entries(ownership[0]).reduce(
      (a, [key, value = 0]): OwnershipDataFormatted => ({
        ...a,
        [key]: {
          shares: value.toLocaleString(locale),
          percent: (value / (totalOwnedShares || 1)).toLocaleString(locale, {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 3,
            maximumSignificantDigits: 3,
          }),
        },
      }),
      {}
    );

    /**
     * Insider Trading
     */
    const insiderBuys = Array.from(
      { length: 4 },
      (n, i): PartialInsiderTradingEntity => {
        const entity = legacyChartData.insiderTrading.buy[i];
        if (entity === undefined) {
          return {
            companySharesBought: NaN,
            individualSharesBought: NaN,
            companyBuyCount: NaN,
            individualBuyCount: NaN,
            totalBought: NaN,
            totalBoughtValue: '',
          };
        }
        return {
          companySharesBought: entity.totalCompanyShares,
          individualSharesBought: entity.totalIndividualShares,
          companyBuyCount: entity.companyCount,
          individualBuyCount: entity.individualCount,
          totalBought: entity.totalShares,
          totalBoughtValue: toCurrencyTruncated(
            entity.totalShares * entity.avgPrice,
            1,
            formatting.tradingItemCurrencyISO
          ),
        };
      }
    );

    const insiderTrading = insiderBuys.map((n, i): InsiderTradingEntity => {
      const entity = legacyChartData.insiderTrading.sell[i];
      if (entity === undefined) {
        return {
          ...n,
          companySharesSold: NaN,
          individualSharesSold: NaN,
          companySellCount: NaN,
          individualSellCount: NaN,
          totalSold: NaN,
          totalSoldValue: '',
        };
      }
      return {
        ...n,
        companySharesSold: Math.abs(entity.totalCompanyShares),
        individualSharesSold: Math.abs(entity.totalIndividualShares),
        companySellCount: entity.companyCount,
        individualSellCount: entity.individualCount,
        totalSold: Math.abs(entity.totalShares),
        totalSoldValue: toCurrencyTruncated(
          Math.abs(entity.totalShares) * entity.avgPrice,
          1,
          formatting.tradingItemCurrencyISO
        ),
      };
    });

    return {
      companyId: summaryNext.companyId,
      uniqueSymbol: summaryNext.uniqueSymbol,
      topShareholders: topShareholdersWithTitles,
      totalOwnershipPercent,
      insiderTrading,
      /**
       * Ownership Chart
       */
      ownership,
      ownershipDataFormatted,
      totalOwnedShares: totalOwnedShares || 0,
    };
  }
);

export const insiderTradingProps = createSelector(
  [getOwnershipSelector],
  (ownership) => {
    return {
      data: ownership.insiderTrading,
    };
  }
);

export const ownershipBreakdownProps = createSelector(
  [getOwnershipSelector],
  (ownership) => {
    return {
      data: ownership.ownership,
      dataFormatted: ownership.ownershipDataFormatted,
      totalOwnedShares: ownership.totalOwnedShares,
    };
  }
);

export { getOwnershipSelector as default };
